import React, { useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useMediaQuery, useTheme } from "@mui/material";

// ── Hook ────────────────────────────────────────────────────────────────────
function useDonutChart(
    ref,
    data,
    padding = { top: 0, bottom: 0, left: 16, right: 16 },
    labelRadius = 4,
    tooltipFontSize = 12,
    tooltipMaxWidth = 220,
    labelFontSize = 12,
    forceLabelsHidden = false,
    visibleCategories = {},
    isMobile = false,
    interactive = false
) {
    useLayoutEffect(() => {
        if (!ref.current) return;

        const root = am5.Root.new(ref.current);
        root._logo?.dispose();
        root.setThemes([am5themes_Animated.new(root)]);

        const chart = root.container.children.push(
            am5percent.PieChart.new(root, {
                width: am5.percent(100),
                height: am5.percent(100),
                innerRadius: am5.percent(75),
                paddingTop: padding.top,
                paddingBottom: padding.bottom,
                paddingLeft: padding.left,
                paddingRight: padding.right,
            })
        );

        const series = chart.series.push(
            am5percent.PieSeries.new(root, {
                valueField: "value",
                categoryField: "category",
                alignLabels: false,
            })
        );

        const tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            autoTextColor: false,
            pointerOrientation: "horizontal",
            followPointer: true
        });
        tooltip.get("background").setAll({
            fill: am5.color(0x6672ff),
            fillOpacity: 0.95,
            stroke: am5.color(0xe2e8f0),
            strokeOpacity: 0.9,
        });
        tooltip.label.setAll({
            fill: am5.color(0xffffff),
            fontSize: tooltipFontSize,
            maxWidth: tooltipMaxWidth,
            oversizedBehavior: "wrap",
            textAlign: "left",
        });

        series.labels.template.setAll({
            forceHidden: forceLabelsHidden,
            text: "{value}%",
            fill: am5.color(0xffffff),
            fontSize: labelFontSize,
            radius: labelRadius
        });

        series.labels.template.adapters.add("forceHidden", (forceHidden, target) => {
            if (interactive) {
                const category = target.dataItem?.get("category");
                return !visibleCategories?.[category];
            }
            return forceHidden;
        });
        series.ticks.template.set("forceHidden", true);
        series.slices.template.setAll({
            stroke: am5.color(0x13172a),
            cornerRadius: 10,
            templateField: "sliceSettings",
            tooltipText: "{category}: {value.formatNumber('0.00')}%",
            tooltip: tooltip,
        });
        
        // Set cursor pointer on the whole chart area for better responsiveness
        root.container.set("cursorOverStyle", "pointer");

        series.slices.template.states.create("hover", { scale: 1.04 });

        series.data.setAll(
            (data || []).map((item) => ({
                ...item,
                sliceSettings: {
                    fill: am5.color(item.color),
                    stroke: am5.color(0x13172a),
                },
            }))
        );

        return () => root.dispose();
    }, [data, padding, labelRadius, tooltipFontSize, tooltipMaxWidth, labelFontSize, forceLabelsHidden, visibleCategories, isMobile, interactive]);
}

// ── Sub-components ───────────────────────────────────────────────────────────
function DonutChart({
    data,
    size = 320,
    centerLabel,
    centerValue,
    centerSubLabel,
    padding,
    labelRadius,
    tooltipFontSize,
    tooltipMaxWidth,
    centerValueFontSize = 30,
    centerLabelFontSize = 13,
    centerSubLabelFontSize = 13,
    centerValueFontWeight = 700,
    labelFontSize = 12,
    isMobile = false,
    forceLabelsHidden = false,
    visibleCategories = {},
    interactive = false,
}) {
    const chartRef = useRef(null);
    useDonutChart(
        chartRef,
        data,
        padding,
        labelRadius,
        tooltipFontSize,
        tooltipMaxWidth,
        labelFontSize,
        forceLabelsHidden,
        visibleCategories,
        isMobile,
        interactive
    );

    return (
        <div style={{
            position: "relative",
            width: size,
            height: size,
            maxWidth: "100%",
            maxHeight: "100%",
            flexShrink: 0,
            overflow: "visible"
        }}>
            <div ref={chartRef} style={{ width: "100%", height: "100%" }} />

            <div style={{
                position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center", pointerEvents: "none", width: "75%",
            }}>
                {centerLabel && <div style={{ fontSize: centerLabelFontSize, color: isMobile ? "#ffffff" : "#8b92a5", marginBottom: 6, whiteSpace: "nowrap", opacity: isMobile ? 0.9 : 1 }}>{centerLabel}</div>}
                {centerValue && <div style={{ fontSize: centerValueFontSize, fontWeight: centerValueFontWeight, color: "#ffffff", marginBottom: 6, whiteSpace: "nowrap" }}>{centerValue}</div>}
                {centerSubLabel && <div style={{ fontSize: centerSubLabelFontSize, color: isMobile ? "#ffffff" : "#8b92a5", lineHeight: 1.4, opacity: isMobile ? 0.8 : 1 }}>{centerSubLabel}</div>}
            </div>
        </div>
    );
}

function ChartLegend({
    data = [],
    gap = 20,
    fontSize = 14,
    isMobile = false,
    visibleCategories = {},
    onToggle,
    onHover, // new prop for explicit visibility control
    interactive = false,
    horizontal = false
}) {
    return (
        <div style={{
            display: "grid",
            gridTemplateColumns: (isMobile || horizontal) ? "repeat(2, 1fr)" : "1fr",
            gap: (isMobile || horizontal) ? "12px 10px" : gap,
            width: "100%"
        }}>
            {data.map(({ category, color, percentage }) => (
                <div
                    key={category}
                    onClick={() => interactive && isMobile && onToggle?.(category)}
                    onMouseEnter={() => interactive && !isMobile && onHover?.(category, true)}
                    onMouseLeave={() => interactive && !isMobile && onHover?.(category, false)}
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        cursor: interactive ? "pointer" : "default",
                        transition: "opacity 0.2s",
                        "&:hover": { opacity: 0.8 }
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0, display: "block", marginTop: 3 }}>
                        <rect x="0" y="0" width="14" height="14" rx="4" fill={color} />
                    </svg>
                    <span style={{
                        fontSize,
                        color: "#ffffff",
                        whiteSpace: "normal",
                        lineHeight: 1.2
                    }}>
                        {category} {interactive && percentage ? `(${percentage}%)` : ""}
                    </span>
                </div>
            ))}
        </div>
    );
}

// ── Main export ──────────────────────────────────────────────────────────────
function WealthChart({
    data,
    centerLabel,
    centerValue,
    centerSubLabel,
    chartSize = 320,
    showLegend = true,        // toggle legend
    legendGap = 16,           // legend item spacing
    padding,                  // { top, bottom, left, right }
    labelRadius,
    centerValueFontSize,
    centerLabelFontSize,
    centerSubLabelFontSize,
    centerValueFontWeight,
    tooltipFontSize,
    tooltipMaxWidth,
    legendFontSize = 14,
    labelFontSize = 12,
    toggleLabelsOnMobile = false,
    legendPosition = "right", // "right" or "bottom"
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const [visibleCategories, setVisibleCategories] = React.useState({});

    const toggleCategory = (category) => {
        setVisibleCategories((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const setCategoryVisibility = (category, isVisible) => {
        setVisibleCategories((prev) => ({
            ...prev,
            [category]: isVisible,
        }));
    };

    const filteredData = (data || []).filter(item => (item.value || 0) > 0);
    const totalValue = filteredData.reduce((acc, item) => acc + (item.value || 0), 0);
    const dataWithPercentage = filteredData.map(item => ({
        ...item,
        percentage: totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(2) : 0
    }));

    const defaultPadding = isMobile
        ? { top: 10, bottom: 10, left: (isXs ? 12 : 15), right: (isXs ? 12 : 15) }
        : { top: 0, bottom: 0, left: 16, right: 16 };

    const isInteractive = toggleLabelsOnMobile || isMobile;
    const isLegendBottom = isMobile || legendPosition === "bottom";

    return (
        <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: isLegendBottom ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            columnGap: "60px",
            rowGap: isLegendBottom ? "8px" : "40px",
            padding: "0px",
        }}
        >
            <DonutChart
                data={dataWithPercentage}
                size={chartSize}
                centerLabel={centerLabel}
                centerValue={centerValue}
                centerSubLabel={centerSubLabel}
                padding={padding || defaultPadding}
                labelRadius={labelRadius ?? (isXs ? 1 : 4)}
                centerValueFontSize={centerValueFontSize ?? (isXs ? 18 : (isMobile ? 22 : 30))}
                centerLabelFontSize={centerLabelFontSize ?? (isXs ? 11 : (isMobile ? 12 : 13))}
                centerSubLabelFontSize={centerSubLabelFontSize ?? (isXs ? 10 : (isMobile ? 11 : 13))}
                centerValueFontWeight={centerValueFontWeight ?? 700}
                tooltipFontSize={tooltipFontSize ?? (isXs ? 10 : 12)}
                tooltipMaxWidth={tooltipMaxWidth ?? 220}
                labelFontSize={labelFontSize ?? (isXs ? 10 : 12)}
                isMobile={isMobile}
                forceLabelsHidden={isInteractive}
                visibleCategories={visibleCategories}
                interactive={isInteractive}
            />
            {showLegend && (
                <div style={{
                    width: isLegendBottom ? "100%" : "auto",
                    maxWidth: "100%",
                    overflow: "visible",
                    padding: isLegendBottom ? "0 10px" : "0px"
                }}>
                    <ChartLegend
                        data={dataWithPercentage}
                        gap={legendGap}
                        fontSize={legendFontSize}
                        isMobile={isMobile}
                        visibleCategories={visibleCategories}
                        onToggle={toggleCategory}
                        onHover={setCategoryVisibility}
                        interactive={isInteractive}
                        horizontal={isLegendBottom}
                    />
                </div>
            )}
        </div>
    );
}

export default WealthChart;